/**
 * Subscription Service - Google Play Billing Integration
 * PRODUCTION VERSION - Real purchases only
 * 
 * Product: mystic_tarot_single_reading (149 TRY, 20 credits, 30 days)
 * Type: INAPP (one-time purchase, consumable)
 */

import axios from 'axios';
import { Capacitor } from '@capacitor/core';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// Platform detection
export const isNativePlatform = () => {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
};

export const getPlatform = () => {
  try {
    return Capacitor.getPlatform(); // 'web', 'ios', 'android'
  } catch {
    return 'web';
  }
};

// Product SKUs - TEK ÜRÜN (INAPP - Tek Seferlik)
export const PRODUCT_SKUS = {
  STARTER: 'mystic_tarot_single_reading'
};

// Product Info
export const SUBSCRIPTION_PLANS = {
  starter: {
    sku: 'mystic_tarot_single_reading',
    name: 'Mystic Starter Pack',
    name_tr: 'Mystic Başlangıç Paketi',
    price: 149,
    currency: 'TRY',
    credits: 20,
    validity_days: 30,
    popular: true,
    type: 'inapp', // One-time purchase
    features_tr: [
      '20 kredi',
      '30 gün geçerli',
      'Tüm özellikler',
      'Kişiye özel yorumlar',
      'Reklamsız'
    ],
    features_en: [
      '20 credits',
      'Valid for 30 days',
      'All features',
      'Personal readings',
      'No ads'
    ]
  }
};

// Credit costs - YENİ FİYATLANDIRMA
export const CREDIT_COSTS = {
  coffee: 3,        // Kahve Falı - 3 kredi
  relationship: 6,  // İlişki Analizi - 6 kredi  
  tarot: 5,         // Tarot - 5 kredi
  horoscope: 1,     // Günlük Burç - 1 kredi
  dream: 4          // Rüya Tabiri - 4 kredi (gelecek özellik)
};

/**
 * Google Play Billing Service
 */
class SubscriptionService {
  constructor() {
    this.isInitialized = false;
    this.nativePurchases = null;
    this.currentUserId = null;
  }

  /**
   * Initialize billing service
   */
  async initialize(userId) {
    if (this.isInitialized) return true;
    
    this.currentUserId = userId;
    
    if (!isNativePlatform() || getPlatform() !== 'android') {
      console.log('[Billing] Not on Android native platform');
      return false;
    }

    try {
      // Dynamically import native purchases plugin
      const { NativePurchases, PURCHASE_TYPE } = await import('@capgo/native-purchases');
      this.nativePurchases = NativePurchases;
      this.PURCHASE_TYPE = PURCHASE_TYPE;
      
      console.log('🔵 [Billing DEBUG] PURCHASE_TYPE available:', {
        INAPP: PURCHASE_TYPE.INAPP,
        SUBS: PURCHASE_TYPE.SUBS
      });
      
      // Check if billing is supported
      const { isBillingSupported } = await NativePurchases.isBillingSupported();
      
      if (!isBillingSupported) {
        console.error('[Billing] Google Play Billing not supported');
        return false;
      }
      
      console.log('✅ [Billing] Google Play Billing initialized successfully');
      this.isInitialized = true;
      return true;
    } catch (error) {
      console.error('❌ [Billing] Initialization error:', error);
      return false;
    }
  }

  /**
   * DEBUG: Get detailed billing diagnostic info
   */
  async getBillingDebugInfo() {
    const debugInfo = {
      timestamp: new Date().toISOString(),
      platform: getPlatform(),
      isNative: isNativePlatform(),
      isInitialized: this.isInitialized,
      configuredSKU: PRODUCT_SKUS.STARTER,
      productType: 'INAPP (one-time purchase)',
      queryResults: null,
      errors: []
    };

    console.log('🔍 ========== BILLING DEBUG START ==========');
    console.log('🔍 [DEBUG] Platform:', debugInfo.platform);
    console.log('🔍 [DEBUG] Is Native:', debugInfo.isNative);
    console.log('🔍 [DEBUG] Is Initialized:', debugInfo.isInitialized);
    console.log('🔍 [DEBUG] Configured SKU:', debugInfo.configuredSKU);
    console.log('🔍 [DEBUG] Product Type: INAPP (tek seferlik satın alma)');

    if (!this.isInitialized || !this.nativePurchases) {
      debugInfo.errors.push('Billing service not initialized');
      console.error('❌ [DEBUG] Billing service not initialized!');
      return debugInfo;
    }

    try {
      // Query the specific product
      console.log('🔍 [DEBUG] Querying product:', PRODUCT_SKUS.STARTER);
      console.log('🔍 [DEBUG] Using ProductType: INAPP');
      
      const { products } = await this.nativePurchases.getProducts({
        productIdentifiers: [PRODUCT_SKUS.STARTER],
        productType: this.PURCHASE_TYPE.INAPP,
      });

      debugInfo.queryResults = {
        productCount: products ? products.length : 0,
        products: products || [],
        queriedSKU: PRODUCT_SKUS.STARTER,
        usedProductType: 'INAPP'
      };

      console.log('🔍 [DEBUG] Query Results:');
      console.log('   - Products found:', products ? products.length : 0);
      
      if (products && products.length > 0) {
        products.forEach((p, i) => {
          console.log(`   - Product ${i + 1}:`, {
            id: p.identifier || p.productId,
            title: p.title,
            price: p.priceString || p.price,
            type: p.type || 'INAPP'
          });
        });
      } else {
        console.warn('⚠️ [DEBUG] NO PRODUCTS RETURNED!');
        console.warn('⚠️ [DEBUG] Possible causes:');
        console.warn('   1. SKU mismatch - check Play Console product ID');
        console.warn('   2. Product not active in Play Console');
        console.warn('   3. App not installed from Play Store');
        console.warn('   4. Test account not configured');
      }

    } catch (error) {
      debugInfo.errors.push(error.message || String(error));
      console.error('❌ [DEBUG] Query error:', error);
      console.error('❌ [DEBUG] Error code:', error.code);
      console.error('❌ [DEBUG] Error message:', error.message);
    }

    console.log('🔍 ========== BILLING DEBUG END ==========');
    console.log('🔍 [DEBUG] Full debug info:', JSON.stringify(debugInfo, null, 2));
    
    return debugInfo;
  }

  /**
   * Get product info from Google Play
   */
  async getProductInfo(productId = PRODUCT_SKUS.STARTER) {
    if (!this.isInitialized || !this.nativePurchases) {
      console.error('[Billing] Service not initialized');
      return null;
    }

    console.log('🛒 [Billing] Fetching product info for:', productId);
    console.log('🛒 [Billing] Using ProductType: INAPP');

    try {
      const { products } = await this.nativePurchases.getProducts({
        productIdentifiers: [productId],
        productType: this.PURCHASE_TYPE.INAPP, // One-time purchase
      });

      console.log('🛒 [Billing] getProducts response:', { 
        count: products?.length || 0, 
        products 
      });

      if (products && products.length > 0) {
        console.log('✅ [Billing] Product found:', products[0]);
        return {
          identifier: products[0].identifier,
          title: products[0].title,
          description: products[0].description,
          price: products[0].priceString,
          priceAmountMicros: products[0].priceAmountMicros,
          priceCurrencyCode: products[0].priceCurrencyCode
        };
      }
      
      console.warn('⚠️ [Billing] Product not found:', productId);
      return null;
    } catch (error) {
      console.error('❌ [Billing] Error fetching product:', error);
      return null;
    }
  }

  /**
   * Purchase product - REAL GOOGLE PLAY BILLING
   * @param {string} productId - Product SKU (mystic_starter_pack)
   */
  async purchaseSubscription(productId = PRODUCT_SKUS.STARTER) {
    console.log('[Billing] Starting purchase for:', productId);

    // Block purchases on web
    if (!isNativePlatform()) {
      throw new Error('WEB_PURCHASE_BLOCKED');
    }

    // Ensure service is initialized
    if (!this.isInitialized) {
      await this.initialize(this.currentUserId);
    }

    if (!this.nativePurchases) {
      throw new Error('BILLING_NOT_AVAILABLE');
    }

    const token = localStorage.getItem('token');
    if (!token) {
      throw new Error('Kullanıcı oturumu bulunamadı. Lütfen tekrar giriş yapın.');
    }

    try {
      // Initiate real Google Play purchase
      console.log('[Billing] Initiating Google Play purchase...');
      
      const transaction = await this.nativePurchases.purchaseProduct({
        productIdentifier: productId,
        productType: this.PURCHASE_TYPE.INAPP,
      });

      console.log('[Billing] Purchase transaction received:', transaction);

      // Validate transaction
      if (!transaction || !transaction.purchaseToken) {
        throw new Error('INVALID_TRANSACTION');
      }

      // Send to backend for verification
      const verificationResult = await this.verifyPurchaseOnBackend(
        transaction.purchaseToken,
        productId,
        transaction.orderId
      );

      return verificationResult;

    } catch (error) {
      console.error('[Billing] Purchase error:', error);

      // Handle specific error codes
      if (error.code === 'USER_CANCELLED' || error.message?.includes('cancelled')) {
        throw new Error('PURCHASE_CANCELLED');
      }
      
      if (error.code === 'BILLING_UNAVAILABLE') {
        throw new Error('BILLING_NOT_AVAILABLE');
      }

      if (error.code === 'ITEM_UNAVAILABLE') {
        throw new Error('PRODUCT_NOT_FOUND');
      }

      throw error;
    }
  }

  /**
   * Verify purchase on backend
   * @param {string} purchaseToken - Google Play purchase token
   * @param {string} productId - Product SKU
   * @param {string} orderId - Google Play order ID
   */
  async verifyPurchaseOnBackend(purchaseToken, productId, orderId) {
    const token = localStorage.getItem('token');
    
    console.log('[Billing] Verifying purchase on backend...');
    console.log('[Billing] Token length:', purchaseToken?.length);
    console.log('[Billing] Product:', productId);
    console.log('[Billing] Order:', orderId);

    try {
      const response = await axios.post(
        `${API_URL}/api/purchase/verify`,
        {
          purchaseToken: purchaseToken,
          productId: productId,
          orderId: orderId,
          platform: 'android'
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      console.log('[Billing] Backend verification response:', response.data);

      if (response.data.success) {
        // Update local storage with new credits
        const userStr = localStorage.getItem('user');
        if (userStr) {
          const user = JSON.parse(userStr);
          user.premium = true;
          user.subscription_type = response.data.subscription_type;
          user.credits = response.data.credits;
          localStorage.setItem('user', JSON.stringify(user));
        }

        return {
          success: true,
          credits: response.data.credits,
          message: response.data.message
        };
      } else {
        throw new Error(response.data.message || 'Verification failed');
      }

    } catch (error) {
      console.error('[Billing] Backend verification error:', error);
      
      if (error.response?.data?.detail) {
        throw new Error(error.response.data.detail);
      }
      
      throw new Error('Satın alma doğrulaması başarısız. Lütfen destek ile iletişime geçin.');
    }
  }

  /**
   * Restore purchases
   */
  async restorePurchases() {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/credits/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    } catch (error) {
      console.error('[Billing] Restore failed:', error);
      throw error;
    }
  }

  /**
   * Check premium status
   */
  async checkPremiumStatus(authToken) {
    try {
      const response = await axios.get(`${API_URL}/api/credits/status`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      return response.data;
    } catch (error) {
      console.error('[Billing] Status check failed:', error);
      return { premium: false, credits: 0 };
    }
  }
}

const subscriptionService = new SubscriptionService();
export default subscriptionService;
